import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend, AreaChart, Area } from 'recharts';
import { DollarSign, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Clock, Send, XCircle, Search, Shield, BarChart3 } from 'lucide-react';
import ReinsurancePage from '@/pages/ReinsurancePage';
import BankReceiptingTab from '@/components/BankReceiptingTab';
import { ExperienceProvenance } from '@/components/ExperienceProvenance';
import { toast } from 'sonner';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', teal: '#00B4D8', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', saffron: '#FF9933' };
const CHART_COLORS = ['#1A4C7C', '#00B4D8', '#1D9E75', '#E8A020', '#E24B4A', '#FF9933', '#7C3AED', '#5C6B7A', '#0EA5E9', '#D946EF'];
function fmt$(v) { if (!v && v !== 0) return '$0'; if (Math.abs(v) >= 1e6) return `$${(v/1e6).toFixed(1)}M`; if (Math.abs(v) >= 1e3) return `$${(v/1e3).toFixed(0)}K`; return `$${Math.round(v).toLocaleString()}`; }

function BookToggleStrip({ api, book, setBook }) {
  const [sum, setSum] = useState(null);
  useEffect(() => {
    api.get(`/finance/summary?book=${book}`).then(r => setSum(r.data)).catch(e => console.error('API error:', e?.message));
  }, [api, book]);
  return (
    <Card className="p-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="finance-book-strip">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex rounded-sm overflow-hidden" style={{ border: '1px solid rgba(136,150,168,0.25)' }}>
          {['live', 'rehearsal', 'combined'].map(b => (
            <button key={b} onClick={() => setBook(b)} data-testid={`finance-book-${b}`}
              className="px-3 py-1.5 text-[11px] font-semibold capitalize transition-colors"
              style={book === b ? { background: C.blue, color: '#fff' } : { color: C.slate, background: '#fff' }}>
              {b}
            </button>
          ))}
        </div>
        {sum && (
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs">
            <span style={{ color: C.slate }}>Policies <strong className="font-mono" style={{ color: C.navy }} data-testid="book-policies">{(sum.active_policies ?? 0).toLocaleString()}</strong></span>
            <span style={{ color: C.slate }}>GWP <strong className="font-mono" style={{ color: C.navy }} data-testid="book-gwp">{fmt$(sum.gwp)}</strong></span>
            <span style={{ color: C.slate }}>Claims Paid <strong className="font-mono" style={{ color: C.navy }} data-testid="book-claims-paid">{fmt$(sum.total_claims_paid)}</strong></span>
            <span style={{ color: C.slate }}>Reserves <strong className="font-mono" style={{ color: C.navy }}>{fmt$(sum.total_reserves)}</strong></span>
            <span style={{ color: C.slate }}>Incurred LR <strong className="font-mono" style={{ color: (sum.loss_ratio || 0) > 75 ? C.red : C.green }} data-testid="book-loss-ratio">{sum.loss_ratio}%</strong></span>
          </div>
        )}
      </div>
    </Card>
  );
}

export default function FinancePage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [chasing, setChasing] = useState(null);
  const [recon, setRecon] = useState(null);
  const [classProfit, setClassProfit] = useState(null);
  const [cashFlow, setCashFlow] = useState(null);
  const [claimsDev, setClaimsDev] = useState(null);
  const [stampDuty, setStampDuty] = useState(null);
  const [reserves, setReserves] = useState(null);
  const [lossRatio, setLossRatio] = useState(null);
  const [lrAlerts, setLrAlerts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [book, setBook] = useState('live');
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') || 'overview');

  // The book toggle drives the class view too — switching to Live and still seeing the combined
  // loss ratios would be the same conflation the integrity sweep was fixed for.
  useEffect(() => {
    api.get(`/finance/class-profitability?book=${book}`)
      .then(r => setClassProfit(r.data)).catch(e => console.error('API error:', e?.message));
  }, [api, book]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.allSettled([
      api.get('/finance/dashboard'), api.get('/finance/payment-chasing'), api.get('/finance/reconciliation'),
      api.get('/finance/cash-flow'),
      api.get('/finance/claims-development'), api.get('/finance/stamp-duty'), api.get('/finance/reserves'),
      api.get('/finance/loss-ratio-analysis'), api.get('/finance/lr-alerts'),
    ]).then(([d, ch, r, cf, cd, sd, res, lr, al]) => {
      const v = (x) => (x.status === 'fulfilled' ? x.value.data : null);
      setData(v(d)); setChasing(v(ch)); setRecon(v(r));
      setCashFlow(v(cf)); setClaimsDev(v(cd));
      setStampDuty(v(sd)); setReserves(v(res)); setLossRatio(v(lr));
      setLrAlerts(v(al));
    }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-16 rounded-md" />)}</div>;
  if (!data) return (
    <div className="p-4 rounded-md text-xs" data-testid="finance-restricted-note"
      style={{ background: 'rgba(136,150,168,0.08)', color: '#5C6B7A' }}>
      Finance reporting — the trust position, loss ratios, reserves and reconciliation — is restricted to
      the MGA admin and the platform owner. Invoices and premium receipting are on the Invoices screen.
    </div>
  );

  const tabs = [
    { id: 'overview', label: 'Overview' }, { id: 'lossratio', label: 'Loss Ratio (Actuarial)' },
    { id: 'pnl', label: 'P&L' },
    { id: 'reinsurance', label: 'Reinsurance' },
    { id: 'class', label: 'Class Profitability' }, { id: 'cashflow', label: 'Cash Flow' },
    { id: 'leakage', label: 'Claims Leakage' }, { id: 'reserves', label: 'Reserves & IBNR' },
    { id: 'brokers', label: 'Broker Statements' }, { id: 'bank', label: 'Bank Receipting' },
    { id: 'chasing', label: 'Payment Chasing' },
    { id: 'tax', label: 'Stamp Duty & GST' }, { id: 'reconciliation', label: 'Reconciliation' },
    { id: 'claimpayments', label: 'Claim Payments' },
  ];

  return (
    <div className="space-y-4" data-testid="finance-page">
      <BookToggleStrip api={api} book={book} setBook={setBook} />
      {lrAlerts?.alerts?.length > 0 && <LRAlertBanner data={lrAlerts} onView={() => setTab('class')} />}
      <div className="flex flex-wrap gap-0 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`finance-tab-${t.id}`}
            className={`px-3 py-2.5 text-[12px] font-medium border-b-2 transition-colors whitespace-nowrap ${tab === t.id ? 'border-[#2A6FC4] text-[#0D1B2A]' : 'border-transparent text-[#5C6B7A] hover:text-[#0D1B2A]'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'overview' && <OverviewTab data={data} />}
      {tab === 'lossratio' && <LossRatioTab data={lossRatio} api={api} />}
      {tab === 'pnl' && <><PnLTab data={data} /><PnlEmailCard api={api} /></>}
      {tab === 'reinsurance' && <ReinsurancePage />}
      {tab === 'class' && <ClassProfitTab data={classProfit} />}
      {tab === 'cashflow' && <CashFlowTab data={cashFlow} />}
      {tab === 'leakage' && <LeakageTab data={claimsDev} />}
      {tab === 'reserves' && <ReservesTab data={reserves} />}
      {tab === 'brokers' && <BrokerStatementsTab data={data} />}
      {tab === 'bank' && <BankReceiptingTab api={api} />}
      {tab === 'chasing' && <ChasingTab chasing={chasing} />}
      {tab === 'tax' && <StampDutyTab data={stampDuty} />}
      {tab === 'reconciliation' && <ReconciliationTab recon={recon} data={data} />}
      {tab === 'claimpayments' && <ClaimPaymentsTab api={api} />}
    </div>
  );
}

// ─── Claim Payments (settlement payment runs) ───
function ClaimPaymentsTab({ api }) {
  const navigate = useNavigate();
  const [hist, setHist] = useState(null);
  const [preview, setPreview] = useState(null);
  const [denied, setDenied] = useState(false);
  const [gap, setGap] = useState(null);
  const [showGap, setShowGap] = useState(false);

  useEffect(() => {
    Promise.all([api.get('/claims/payment-run/history'), api.get('/claims/payment-run/preview')])
      .then(([h, p]) => { setHist(h.data); setPreview(p.data); })
      .catch(e => { if (e?.response?.status === 403) setDenied(true); });
  }, [api]);

  const downloadCsv = async (runId) => {
    try {
      const res = await api.get(`/claims/payment-run/${runId}/csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url; a.download = `claim-payment-run-${runId}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error('Could not rebuild that run — no claims found for it'); }
  };

  if (denied) return <Card className="p-6 rounded-md text-sm" style={{ color: C.slate }}>Claim payment runs are visible to MGA admins only.</Card>;
  if (!hist || !preview) return <Skeleton className="h-40 rounded-md" />;

  const unreconciled = Math.round((hist.ledger_paid_total - hist.exported_total - preview.total - (preview.held_total || 0)) * 100) / 100;
  const stats = [
    { label: 'Claims ledger paid', value: fmt$(hist.ledger_paid_total), sub: `${hist.ledger_paid_claims} real claims`, color: C.navy },
    { label: 'Exported to bank', value: fmt$(hist.exported_total), sub: `${hist.exported_claims} payments · ${hist.runs.length} runs`, color: C.green },
    { label: 'Awaiting export', value: fmt$(preview.total), sub: `${preview.count} settled claims`, color: C.blue },
    { label: 'Held on discharge', value: fmt$(preview.held_total || 0), sub: `${preview.held || 0} awaiting signed return`, color: C.amber },
  ];

  return (
    <div className="space-y-4" data-testid="claim-payments-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {stats.map(s => (
          <Card key={s.label} className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
            <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{s.label}</p>
            <p className="text-xl font-bold mt-1" style={{ color: s.color }} data-testid={`claim-pay-stat-${s.label.toLowerCase().replace(/ /g, '-')}`}>{s.value}</p>
            <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>{s.sub}</p>
          </Card>
        ))}
      </div>
      <Card onClick={async () => {
          if (Math.abs(unreconciled) < 1) return;
          if (!gap) { try { const r = await api.get('/claims/payment-run/unreconciled'); setGap(r.data); } catch (e) { return; } }
          setShowGap(v => !v);
        }}
        className={`p-3 rounded-md flex items-center gap-2 text-xs ${Math.abs(unreconciled) >= 1 ? 'cursor-pointer' : ''}`} data-testid="claim-pay-recon-bar"
        style={{ border: `1px solid ${Math.abs(unreconciled) < 1 ? 'rgba(29,158,117,0.3)' : 'rgba(232,160,32,0.35)'}`,
                 background: Math.abs(unreconciled) < 1 ? 'rgba(29,158,117,0.04)' : 'rgba(232,160,32,0.05)' }}>
        {Math.abs(unreconciled) < 1
          ? <><CheckCircle className="w-4 h-4" style={{ color: C.green }} /><span style={{ color: C.navy }}><b>Fully reconciled</b> — every real settlement is exported, queued for the next run, or held pending discharge.</span></>
          : <><AlertTriangle className="w-4 h-4" style={{ color: C.amber }} /><span style={{ color: C.navy }}><b>{fmt$(Math.abs(unreconciled))} unreconciled</b> — paid on the claims ledger but not covered by an export, the pending run or a discharge hold (likely settled before payment rails went live).</span>
            <span className="ml-auto font-semibold whitespace-nowrap" style={{ color: '#B27516' }}>{showGap ? 'Hide claims' : 'View claims'}</span></>}
      </Card>
      {showGap && gap && (
        <Card className="rounded-md bg-white" style={{ border: '1px solid rgba(232,160,32,0.35)' }} data-testid="claim-pay-gap-table">
          <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
            <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Unreconciled Claims — {gap.count} · {fmt$(gap.total)}</h3>
          </div>
          {gap.claims.length === 0 ? (
            <p className="p-4 text-xs" style={{ color: C.slate }}>No unreconciled claims found.</p>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
                  {['Claim', 'Insured', 'Status', 'Paid', 'Closed'].map(h => (
                    <th key={h} className="text-left py-2 px-4 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gap.claims.map(c => (
                  <tr key={c.claim_id} onClick={() => navigate(`/claims/${c.claim_id}`, { state: { from: 'finance' } })}
                    className="border-b cursor-pointer hover:bg-[#F5F7FA]"
                    title="Open the full claim file"
                    style={{ borderColor: 'rgba(136,150,168,0.08)' }} data-testid={`gap-claim-${c.claim_reference}`}>
                    <td className="py-2.5 px-4 font-mono font-semibold" style={{ color: C.blue }}>{c.claim_reference}</td>
                    <td className="py-2.5 px-4" style={{ color: C.navy }}>{c.business_name}</td>
                    <td className="py-2.5 px-4 capitalize" style={{ color: C.slate }}>{(c.status || '').replace(/_/g, ' ')}</td>
                    <td className="py-2.5 px-4 font-semibold" style={{ color: C.amber }}>${(c.paid_amount || 0).toLocaleString()}</td>
                    <td className="py-2.5 px-4" style={{ color: C.slate }}>{(c.closed_at || c.updated_at || '').slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
      <Card className="rounded-md bg-white" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="px-4 py-3 border-b" style={{ borderColor: 'rgba(136,150,168,0.12)' }}>
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Payment Run History</h3>
        </div>
        {hist.runs.length === 0 ? (
          <p className="p-4 text-xs" style={{ color: C.slate }}>No payment runs exported yet — export the first one from the Claims page.</p>
        ) : (
          <table className="w-full text-xs" data-testid="claim-pay-runs-table">
            <thead>
              <tr className="border-b" style={{ background: '#FAFBFC', borderColor: 'rgba(136,150,168,0.1)' }}>
                {['Run', 'Date', 'Claims', 'Total', 'Exported By', ''].map(h => (
                  <th key={h} className="text-left py-2 px-4 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {hist.runs.map(r => (
                <tr key={r.run_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
                  <td className="py-2.5 px-4 font-mono font-semibold" style={{ color: C.navy }}>{r.run_id}</td>
                  <td className="py-2.5 px-4" style={{ color: C.slate }}>{(r.at || '').slice(0, 10)}</td>
                  <td className="py-2.5 px-4" style={{ color: C.navy }}>{r.claims}</td>
                  <td className="py-2.5 px-4 font-semibold" style={{ color: C.green }}>${(r.total || 0).toLocaleString()}</td>
                  <td className="py-2.5 px-4" style={{ color: C.slate }}>{r.by}</td>
                  <td className="py-2.5 px-4 text-right">
                    <button onClick={() => downloadCsv(r.run_id)} data-testid={`claim-pay-download-${r.run_id}`}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold"
                      style={{ background: 'rgba(29,158,117,0.1)', color: C.green }}>
                      CSV
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {hist.runs.length > 0 && (
          <p className="px-4 py-2 text-[10px]" style={{ color: C.slate }}>Run totals reflect the original export at the time it was generated; the stats above are computed live from the claims ledger.</p>
        )}
      </Card>
    </div>
  );
}

// ─── Monthly P&L Email ───
function PnlEmailCard({ api }) {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get('/finance/pnl-email').then(r => setStatus(r.data)).catch(() => {}); }, [api]);
  if (!status) return null;
  const p = status.preview || {};
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post('/finance/pnl-email/send');
      if (data.sent) { toast.success(`Monthly P&L for ${data.period} sent to ${status.recipient}`); }
      else { toast.error(`Not sent: ${data.reason}`); }
      const r = await api.get('/finance/pnl-email'); setStatus(r.data);
    } catch { toast.error('Failed to send P&L email'); }
    setSending(false);
  };
  return (
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="pnl-email-card">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex-1 min-w-[240px]">
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Monthly P&L Email</h3>
          <p className="text-[11px] mt-0.5" style={{ color: C.slate }}>
            One-page snapshot emailed automatically on the 1st of each month (AEST)
            {status.recipient ? <> to <strong>{status.recipient}</strong></> : ' — recipient not configured'}
            {status.last?.sent && <> · last sent {status.last.period} ({new Date(status.last.sent_at).toLocaleDateString('en-AU')})</>}
          </p>
        </div>
        <div className="flex items-center gap-4 text-[11px]" style={{ color: C.slate }}>
          <span>{p.period_label}: GWP <strong className="font-mono" style={{ color: C.navy }} data-testid="pnl-email-gwp">{fmt$(p.gwp_written)}</strong></span>
          <span>UW margin (pre-expenses) <strong className="font-mono" style={{ color: (p.underwriting_margin_pre_expenses || 0) >= 0 ? C.green : C.red }} data-testid="pnl-email-uw">{fmt$(p.underwriting_margin_pre_expenses)}</strong></span>
        </div>
        <button onClick={send} disabled={sending || !status.configured} data-testid="pnl-email-send-btn"
          className="px-3.5 py-2 rounded-md text-xs font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: C.blue }}>
          {sending ? 'Sending…' : 'Send now'}
        </button>
      </div>
    </Card>
  );
}

// ─── Overview ───
function LRAlertBanner({ data, onView }) {
  const breach = data.alerts.some(a => a.level === 'breach');
  const tone = breach ? C.red : C.amber;
  return (
    <Card className="p-3 rounded-md" style={{ border: `1px solid ${tone}55`, background: breach ? '#FDF1F1' : '#FDF7EC' }} data-testid="lr-alert-banner">
      <div className="flex flex-wrap items-center gap-3">
        <AlertTriangle className="w-4 h-4 shrink-0" style={{ color: tone }} />
        <span className="text-xs font-semibold" style={{ color: C.navy }}>
          {breach ? 'Loss ratio ceiling breached' : `Loss ratio drift — within 5 pts of ${data.ceiling}% ceiling`}
        </span>
        <div className="flex flex-wrap gap-2">
          {data.alerts.map(a => (
            <span key={a.class} className="px-2 py-0.5 rounded-sm text-[11px] font-mono font-semibold"
              style={{ background: '#fff', border: `1px solid ${a.level === 'breach' ? C.red : C.amber}55`, color: a.level === 'breach' ? C.red : C.amber }}
              data-testid={`lr-alert-${a.class.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
              {a.class} {a.loss_ratio}%
            </span>
          ))}
        </div>
        <button onClick={onView} className="ml-auto text-[11px] font-semibold underline" style={{ color: C.blue }} data-testid="lr-alert-view-btn">
          Review classes
        </button>
      </div>
    </Card>
  );
}

function OverviewTab({ data }) {
  const s = data.summary; const w = data.premium_waterfall;
  const waterfallChart = [
    { name: 'GWP', value: w.gwp, fill: C.navy },
    { name: 'Broker Comm', value: -w.broker_commission, fill: C.red },
    { name: 'MGA Comm', value: -w.mga_commission, fill: C.amber },
    { name: 'Reinsurance', value: -w.ceded_premium, fill: C.slate },
    { name: 'Retained', value: w.retained_premium, fill: C.green },
  ];
  const AGE_LABEL = { current: 'Not yet due', days_1_30: '1-30d late', days_31_60: '31-60d late', days_61_90: '61-90d late', days_90_plus: '90d+ late' };
  const rec = data.receivables || {};
  const ageingChart = Object.entries(rec.buckets || data.ageing).map(([k, v]) => ({ name: AGE_LABEL[k] || k, value: v, count: (rec.counts || {})[k] || 0 }));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <KPI label="GWP" value={fmt$(s.gwp)} color={C.navy} />
        <KPI label="Insured Clients" value={(s.insured_clients || 0).toLocaleString()} color={C.blue} />
        <KPI label="Retained" value={fmt$(s.retained_premium)} color={C.blue} />
        <KPI label="Incurred LR" value={`${s.loss_ratio}%`} color={s.loss_ratio > 80 ? C.red : s.loss_ratio > 60 ? C.amber : C.green} />
        <KPI label="Net LR" value={`${s.loss_ratio_net}%`} color={s.loss_ratio_net > 80 ? C.red : s.loss_ratio_net > 60 ? C.amber : C.green} />
        {s.combined_ratio != null
          ? <KPI label="Combined Ratio" value={`${s.combined_ratio}%`} color={s.combined_ratio > 100 ? C.red : C.green} />
          : <KPI label="LR + Commission (pre-expenses)" value={`${s.combined_ratio_pre_expenses}%`} color={s.combined_ratio_pre_expenses > 100 ? C.red : C.green} />}
        <KPI label={s.underwriting_result != null ? 'UW Result (after expenses)' : 'UW Margin (pre-expenses)'}
          value={fmt$(s.underwriting_result != null ? s.underwriting_result : s.underwriting_margin_before_expenses)}
          color={(s.underwriting_result != null ? s.underwriting_result : s.underwriting_margin_before_expenses) >= 0 ? C.green : C.red} />
      </div>
      <p className="text-[10px]" style={{ color: C.slate }} data-testid="finance-ratio-basis">
        Commission {s.commission_ratio}% as invoiced
        {s.commission_not_recorded_invoices > 0 && <span style={{ color: C.amber }} data-testid="finance-commission-not-recorded"> · {s.commission_not_recorded_invoices} invoice(s) raised with commission not recorded</span>}
        {s.expense_ratio != null && ` · Expense ratio ${s.expense_ratio}%`} · {s.combined_ratio_basis} · {s.loss_ratio_net_basis}
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Premium Waterfall</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={waterfallChart}><CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" /><XAxis dataKey="name" tick={{ fontSize: 10, fill: C.slate }} /><YAxis tick={{ fontSize: 10, fill: C.slate }} tickFormatter={v => fmt$(v)} /><Tooltip formatter={v => fmt$(Math.abs(v))} /><Bar dataKey="value">{waterfallChart.map((e, i) => <Cell key={i} fill={e.fill} />)}</Bar></BarChart>
          </ResponsiveContainer>
        </Card>
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Receivables Ageing</h3>
          <p className="text-[10px] mb-2" style={{ color: C.slate }} data-testid="ageing-basis-note">
            Outstanding invoices by days past their due date — {fmt$(rec.total_outstanding)} owed on {Object.values(rec.counts || {}).reduce((a, b) => a + b, 0)} invoice(s) on 30/60/90 broker terms
          </p>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={ageingChart}><CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" /><XAxis dataKey="name" tick={{ fontSize: 9, fill: C.slate }} /><YAxis tick={{ fontSize: 10, fill: C.slate }} tickFormatter={v => fmt$(v)} /><Tooltip formatter={(v, n, e) => [`${fmt$(v)} · ${e?.payload?.count || 0} invoice(s)`, 'Outstanding']} /><Bar dataKey="value" fill={C.blue} radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
          {data.receipts && (
            <p className="text-[10px] mt-1" style={{ color: C.slate }} data-testid="collection-evidence-note">
              Collected with a receipt behind it: <b style={{ color: C.green }}>{fmt$(data.receipts.evidenced_total)}</b> ·
              inherited as paid with no receipt: <b style={{ color: C.amber }}>{fmt$(data.receipts.assumed_total)}</b> — reported apart, never combined
            </p>
          )}
        </Card>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Claims Paid" value={fmt$(s.claims_paid)} color={C.blue} />
        <KPI label="Reserves" value={fmt$(s.claims_reserves)} color={C.amber} />
        <KPI label="Collection Rate" value={`${data.invoicing.collection_rate}%`} color={C.green} />
        <KPI label="Outstanding" value={fmt$(data.invoicing.outstanding)} color={C.amber} />
      </div>
    </div>
  );
}

// ─── P&L ───
function PnLTab({ data }) {
  const pnl = data.pnl;
  return (<Card className="p-5 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
    <h3 className="text-xs font-semibold uppercase tracking-wider mb-4" style={{ color: C.navy }}>Profit & Loss Statement</h3>
    <Sect title="Revenue"><PRow label="Gross Written Premium" value={pnl.revenue.gross_written_premium} bold /><PRow label="Less Broker Commission" value={pnl.revenue.less_broker_commission} neg /><PRow label="Less MGA Commission" value={pnl.revenue.less_mga_commission} neg /><PRow label="Net Written Premium" value={pnl.revenue.net_written_premium} bold color={C.blue} /><PRow label="Net Earned Premium" value={pnl.revenue.net_earned_premium} bold color={C.blue} /></Sect>
    <Sect title="Reinsurance"><PRow label="Ceded Premium" value={pnl.reinsurance.ceded_premium} neg /><PRow label="Retained Premium" value={pnl.reinsurance.retained_premium} bold color={C.green} /></Sect>
    <Sect title="Claims"><PRow label="Claims Paid" value={pnl.claims.claims_paid} neg /><PRow label="Claims Reserves" value={pnl.claims.claims_reserves} neg /><PRow label="Total Claims Incurred" value={pnl.claims.claims_incurred} bold neg color={C.red} /></Sect>
    <Sect title="Operating Expenses">
      {data.total_opex != null ? (
        <>
          {Object.entries(pnl.expenses).map(([k, v]) => <PRow key={k} label={k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} value={v} neg />)}
          <PRow label="Total Operating Expenses (earned to date)" value={data.total_opex} bold neg />
          <p className="text-[10px] py-1" style={{ color: C.slate }} data-testid="pnl-opex-basis">{pnl.expenses_basis}</p>
        </>
      ) : <p className="text-xs py-1" style={{ color: C.amber }} data-testid="pnl-opex-not-recorded">{pnl.expenses_basis}</p>}
    </Sect>
    <div className="mt-4 pt-3 border-t-2 flex justify-between" style={{ borderColor: C.navy }}><span className="text-sm font-bold" style={{ color: C.navy }}>Underwriting Margin before expenses</span><span className="text-lg font-bold font-mono" data-testid="pnl-uw-margin" style={{ color: pnl.underwriting_margin_before_expenses >= 0 ? C.green : C.red }}>{pnl.underwriting_margin_before_expenses >= 0 ? '' : '-'}{fmt$(Math.abs(pnl.underwriting_margin_before_expenses))}</span></div>
    {pnl.underwriting_result != null && (
      <div className="mt-2 pt-2 border-t flex justify-between" style={{ borderColor: 'rgba(136,150,168,0.2)' }}><span className="text-sm font-bold" style={{ color: C.navy }}>Underwriting Result after expenses</span><span className="text-lg font-bold font-mono" data-testid="pnl-uw-result" style={{ color: pnl.underwriting_result >= 0 ? C.green : C.red }}>{pnl.underwriting_result >= 0 ? '' : '-'}{fmt$(Math.abs(pnl.underwriting_result))}</span></div>
    )}
    <p className="text-[10px] mt-2" style={{ color: C.slate }}>{pnl.underwriting_margin_basis}</p>
  </Card>);
}

// ─── Class Profitability ───
function ClassProfitTab({ data }) {
  if (!data) return null;
  const chartData = data.classes.map(c => ({ name: c.class.length > 15 ? c.class.slice(0, 15) + '...' : c.class, 'Loss Ratio': c.loss_ratio, 'LR + Commission': c.combined_ratio_pre_expenses, gwp: c.gwp }));
  const mix = data.book_mix;
  return (<div className="space-y-4">
    {mix && (
      <div className="p-3 rounded-md" data-testid="class-profit-book-mix"
        style={{ background: '#FDF7EC', borderLeft: `3px solid ${C.amber}` }}>
        <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: C.amber }}>
          {data.book === 'live'
            ? 'Live book only — no rehearsal premium in these ratios'
            : data.book === 'rehearsal'
              ? 'Rehearsal book only — practice data, not the live book'
              : `${Number(mix.rehearsal_pct_of_gwp).toFixed(1)}% of the premium in this view is the rehearsal book`}
        </p>
        <p className="text-[11px] mt-1" style={{ color: C.slate }}>
          Live: {mix.live.policies.toLocaleString()} policies · ${Math.round(mix.live.gwp).toLocaleString()} ·
          Rehearsal: {mix.rehearsal.policies.toLocaleString()} policies · ${Math.round(mix.rehearsal.gwp).toLocaleString()}.
          {data.book === 'live'
            ? ' The live book is measured on its own experience — where any of that experience is seeded, the note below says so and states the share.'
            : ' Loss ratios are earned across whichever book is selected above. Records dispositioned as platform test artifacts are in neither.'}
        </p>
      </div>
    )}
    <ExperienceProvenance data={data.experience_provenance} testId="class-profit-provenance" />
    <div className="grid grid-cols-3 gap-3">
      <KPI label="Profitable Classes" value={`${data.summary.profitable_classes} / ${data.summary.total_classes}`} color={C.green} />
      <KPI label="Best Class" value={data.summary.best_class} color={C.green} />
      <KPI label="Worst Class" value={data.summary.worst_class} color={C.red} />
    </div>
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Loss Ratio and Loss + Commission by Class (before operating expenses)</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={chartData} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" /><XAxis type="number" tick={{ fontSize: 10, fill: C.slate }} /><YAxis dataKey="name" type="category" tick={{ fontSize: 9, fill: C.slate }} width={120} /><Tooltip /><Bar dataKey="Loss Ratio" fill={C.red} stackId="a" /><Bar dataKey="LR + Commission" fill={C.amber} /><Legend /></BarChart>
      </ResponsiveContainer>
    </Card>
    <Card className="rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <table className="w-full text-xs"><thead><tr style={{ background: '#FAFBFC' }} className="border-b">
        {['Class', 'Policies', 'GWP', 'Claims', 'Loss Ratio', 'LR + Comm.', 'Frequency', 'Severity', 'UW Margin (pre-exp.)', ''].map(h => <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
      </tr></thead><tbody>
        {data.classes.map(c => (
          <tr key={c.class} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
            <td className="py-2 px-3 font-medium" style={{ color: C.navy }}>{c.class}</td>
            <td className="py-2 px-3 font-mono">{c.policies}</td>
            <td className="py-2 px-3 font-mono">{fmt$(c.gwp)}</td>
            <td className="py-2 px-3 font-mono">{c.claims_count}</td>
            <td className="py-2 px-3 font-mono" style={{ color: c.loss_ratio > 70 ? C.red : C.green }}>{c.loss_ratio}%</td>
            <td className="py-2 px-3 font-mono font-semibold" style={{ color: c.combined_ratio_pre_expenses > 100 ? C.red : C.green }}>{c.combined_ratio_pre_expenses}%</td>
            <td className="py-2 px-3 font-mono">{c.frequency}%</td>
            <td className="py-2 px-3 font-mono">{fmt$(c.severity)}</td>
            <td className="py-2 px-3 font-mono font-semibold" style={{ color: c.uw_margin_pre_expenses >= 0 ? C.green : C.red }}>{fmt$(c.uw_margin_pre_expenses)}</td>
            <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-[9px] font-bold" style={{ background: (c.profitable ? C.green : C.red) + '12', color: c.profitable ? C.green : C.red }}>{c.profitable ? 'Profit' : 'Loss'}</span></td>
          </tr>
        ))}
      </tbody></table>
    </Card>
  </div>);
}

// ─── Cash Flow ───
function CashFlowTab({ data }) {
  if (!data) return null;
  const chartData = data.forecast.map(p => ({ name: p.period, Inflows: p.total_inflows, Outflows: -p.total_outflows, Balance: p.closing_balance }));
  return (<div className="space-y-4">
    <div className="grid grid-cols-3 gap-3">
      <KPI label="Trust Balance" value={fmt$(data.current_balance)} color={C.blue} />
      <KPI label="90-Day Projected" value={fmt$(data.projected_90_day)} color={data.projected_90_day >= 0 ? C.green : C.red} />
      <KPI label="Open Claim Reserves" value={fmt$(data.claims_reserves_outstanding)} color={C.amber} />
    </div>
    <p className="text-[10px]" style={{ color: C.slate }} data-testid="cashflow-basis">
      {data.projected_basis} {data.claims_reserves_basis} Overdue now: {fmt$(data.overdue_now?.premium_due)} on {data.overdue_now?.invoices} invoice(s).
    </p>
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>90-Day Cash Flow Forecast</h3>
      <ResponsiveContainer width="100%" height={280}>
        <AreaChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" /><XAxis dataKey="name" tick={{ fontSize: 10, fill: C.slate }} /><YAxis tick={{ fontSize: 10, fill: C.slate }} tickFormatter={v => fmt$(v)} /><Tooltip formatter={v => fmt$(v)} /><Area type="monotone" dataKey="Balance" stroke={C.blue} fill={C.blue + '20'} /><Area type="monotone" dataKey="Inflows" stroke={C.green} fill={C.green + '15'} /><Legend /></AreaChart>
      </ResponsiveContainer>
    </Card>
    {data.forecast.map(p => (
      <Card key={p.period} className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <div className="flex items-center justify-between mb-2"><h4 className="text-sm font-semibold" style={{ color: C.navy }}>{p.period}</h4><span className="text-sm font-mono font-bold" style={{ color: p.net_cash_flow >= 0 ? C.green : C.red }}>{fmt$(p.net_cash_flow)} net</span></div>
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div><p className="text-[10px] uppercase mb-1" style={{ color: C.green }}>Inflows</p>{Object.entries(p.inflows).map(([k, v]) => <div key={k} className="flex justify-between py-0.5"><span style={{ color: C.slate }}>{k.replace(/_/g, ' ')}</span><span className="font-mono" style={{ color: C.green }}>{fmt$(v)}</span></div>)}</div>
          <div><p className="text-[10px] uppercase mb-1" style={{ color: C.red }}>Outflows</p>{Object.entries(p.outflows).map(([k, v]) => <div key={k} className="flex justify-between py-0.5"><span style={{ color: C.slate }}>{k.replace(/_/g, ' ')}</span><span className="font-mono" style={{ color: C.red }}>({fmt$(v)})</span></div>)}</div>
        </div>
      </Card>
    ))}
  </div>);
}

// ─── Claims Leakage ───
function LeakageTab({ data }) {
  if (!data) return null;
  const pieData = [{ name: 'Accurate', value: data.accurate, fill: C.green }, { name: 'Over-Reserved', value: data.over_reserved, fill: C.amber }, { name: 'Under-Reserved', value: data.under_reserved, fill: C.red }];
  return (<div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KPI label="Accuracy Rate" value={`${data.accuracy_rate}%`} color={data.accuracy_rate > 70 ? C.green : C.amber} />
      <KPI label="Over-Reserved" value={data.over_reserved} color={C.amber} />
      <KPI label="Under-Reserved" value={data.under_reserved} color={C.red} />
      <KPI label="Total Leakage" value={fmt$(data.total_leakage)} color={data.total_leakage > 0 ? C.red : C.green} />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Reserve Accuracy</h3>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart><Pie data={pieData} dataKey="value" cx="50%" cy="50%" innerRadius={50} outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>{pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}</Pie><Tooltip /></PieChart>
        </ResponsiveContainer>
      </Card>
      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Leakage by Quantum Band</h3>
        {Object.entries(data.by_band).map(([band, vals]) => (
          <div key={band} className="flex items-center justify-between py-2 border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
            <span className="text-xs font-medium" style={{ color: C.navy }}>{band}</span>
            <div className="flex gap-3 text-[10px]">
              <span style={{ color: C.amber }}>Over: {vals.over}</span>
              <span style={{ color: C.red }}>Under: {vals.under}</span>
              <span style={{ color: C.slate }}>Total: {vals.count}</span>
            </div>
          </div>
        ))}
      </Card>
    </div>
  </div>);
}

// ─── Reserves & IBNR ───
function ReservesTab({ data }) {
  if (!data) return null;
  return (<div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KPI label="Outstanding Reserves" value={fmt$(data.outstanding_reserves)} color={C.amber} />
      <KPI label="IBNR Estimate" value={fmt$(data.ibnr.total)} color={C.red} />
      <KPI label="UPR" value={fmt$(data.upr)} color={C.blue} />
      <KPI label="Total Technical" value={fmt$(data.total_technical_reserves)} color={C.navy} />
    </div>
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Technical Reserves Breakdown</h3>
      <div className="space-y-2">
        <PRow label="Outstanding Case Reserves" value={data.outstanding_reserves} bold />
        <PRow label="IBNR (estimate — see note)" value={data.ibnr.total} bold color={C.red} />
        <p className="text-[10px] pl-1" style={{ color: C.slate }} data-testid="ibnr-basis">{data.ibnr.note}</p>
        <PRow label="Unearned Premium Reserve (UPR)" value={data.upr} />
        <p className="text-[10px] pl-1" style={{ color: C.slate }}>{data.upr_basis}</p>
        <PRow label="Deferred Acquisition Costs (DAC)" value={data.dac} />
        <p className="text-[10px] pl-1" style={{ color: C.slate }}>{data.dac_basis}</p>
        <div className="mt-2 pt-2 border-t flex justify-between" style={{ borderColor: 'rgba(136,150,168,0.15)' }}>
          <span className="text-xs font-bold" style={{ color: C.navy }}>Total Technical Reserves</span>
          <span className="text-sm font-bold font-mono" style={{ color: C.navy }}>{fmt$(data.total_technical_reserves)}</span>
        </div>
      </div>
      <p className="text-[10px] mt-3 p-2 rounded" style={{ background: C.bg, color: C.slate }}>{data.ibnr.note}</p>
    </Card>
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: C.navy }}>Reserve Adequacy</h3>
      <div className="flex items-center gap-3">
        <div className="flex-1 h-3 rounded-full" style={{ background: C.bg }}><div className="h-3 rounded-full" style={{ width: `${Math.min(data.reserve_adequacy, 100)}%`, background: data.reserve_adequacy > 80 ? C.green : C.amber }} /></div>
        <span className="text-sm font-mono font-bold" style={{ color: data.reserve_adequacy > 80 ? C.green : C.amber }}>{data.reserve_adequacy}%</span>
      </div>
    </Card>
  </div>);
}

// ─── Stamp Duty & GST ───
function StampDutyTab({ data }) {
  if (!data) return null;
  const chartData = data.states.filter(s => s.state !== 'Unknown').map(s => ({ name: s.state, 'Stamp Duty': s.stamp_duty, GST: s.gst }));
  return (<div className="space-y-4">
    <div className="grid grid-cols-3 gap-3">
      <KPI label="Total Stamp Duty" value={fmt$(data.total_stamp_duty)} color={C.amber} />
      <KPI label="Total GST" value={fmt$(data.total_gst)} color={C.blue} />
      <KPI label="Total Tax Liability" value={fmt$(data.total_tax)} color={C.red} />
    </div>
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Tax by State</h3>
      <ResponsiveContainer width="100%" height={250}>
        <BarChart data={chartData}><CartesianGrid strokeDasharray="3 3" stroke="#E4E7EC" /><XAxis dataKey="name" tick={{ fontSize: 10, fill: C.slate }} /><YAxis tick={{ fontSize: 10, fill: C.slate }} tickFormatter={v => fmt$(v)} /><Tooltip formatter={v => fmt$(v)} /><Bar dataKey="Stamp Duty" fill={C.amber} /><Bar dataKey="GST" fill={C.blue} /><Legend /></BarChart>
      </ResponsiveContainer>
    </Card>
    <Card className="rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
      <table className="w-full text-xs"><thead><tr style={{ background: '#FAFBFC' }} className="border-b">
        {['State', 'Policies', 'GWP', 'SD Rate', 'Stamp Duty', 'GST', 'Total Tax', 'Due'].map(h => <th key={h} className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
      </tr></thead><tbody>
        {data.states.map(s => (
          <tr key={s.state} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
            <td className="py-2 px-3 font-medium" style={{ color: C.navy }}>{s.state}</td>
            <td className="py-2 px-3 font-mono">{s.policies}</td>
            <td className="py-2 px-3 font-mono">{fmt$(s.gwp)}</td>
            <td className="py-2 px-3 font-mono">{(s.stamp_duty_rate * 100).toFixed(0)}%</td>
            <td className="py-2 px-3 font-mono" style={{ color: C.amber }}>{fmt$(s.stamp_duty)}</td>
            <td className="py-2 px-3 font-mono" style={{ color: C.blue }}>{fmt$(s.gst)}</td>
            <td className="py-2 px-3 font-mono font-semibold" style={{ color: C.red }}>{fmt$(s.total_tax)}</td>
            <td className="py-2 px-3" style={{ color: C.slate }}>{s.next_due}</td>
          </tr>
        ))}
      </tbody></table>
    </Card>
  </div>);
}

// ─── Broker Statements, Payment Chasing, Reconciliation (kept from previous) ───
function BrokerStatementsTab({ data }) {
  const [search, setSearch] = useState('');
  const filtered = search ? data.broker_statements.filter(b => b.broker.toLowerCase().includes(search.toLowerCase())) : data.broker_statements;
  return (<div className="space-y-4"><div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: C.slate }} /><input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search broker..." className="w-full pl-10 pr-4 py-2 text-sm rounded-md border" style={{ borderColor: 'rgba(136,150,168,0.2)' }} /></div>
    <Card className="rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}><table className="w-full text-xs"><thead><tr style={{ background: '#FAFBFC' }} className="border-b">{['Broker', 'Policies', 'GWP', 'Commission', 'Net Due', 'Paid', 'Outstanding', 'Status'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}</tr></thead><tbody>{filtered.map(b => (<tr key={b.broker} className="border-b hover:bg-[#FAFBFC]" style={{ borderColor: 'rgba(136,150,168,0.06)' }}><td className="py-2.5 px-3 font-medium" style={{ color: C.navy }}>{b.broker}</td><td className="py-2.5 px-3 font-mono">{b.policies}</td><td className="py-2.5 px-3 font-mono">{fmt$(b.gwp)}</td><td className="py-2.5 px-3 font-mono" style={{ color: C.amber }}>{fmt$(b.commission)}</td><td className="py-2.5 px-3 font-mono font-medium" style={{ color: C.navy }}>{fmt$(b.net_due)}</td><td className="py-2.5 px-3 font-mono" style={{ color: C.green }}>{fmt$(b.paid)}</td><td className="py-2.5 px-3 font-mono" style={{ color: b.outstanding > 0 ? C.red : C.green }}>{fmt$(b.outstanding)}</td><td className="py-2.5 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: (b.status === 'current' ? C.green : C.red) + '12', color: b.status === 'current' ? C.green : C.red }}>{b.status}</span></td></tr>))}</tbody></table></Card></div>);
}
function ChasingTab({ chasing }) {
  if (!chasing) return null;
  const uc = { critical: { color: C.red, label: 'Cancel Warning' }, high: { color: C.amber, label: 'Escalate' }, medium: { color: C.teal, label: 'Chase' } };
  return (<div className="space-y-4"><div className="grid grid-cols-2 md:grid-cols-4 gap-3"><KPI label="Total Overdue" value={chasing.total_overdue} color={C.red} /><KPI label="Overdue Value" value={fmt$(chasing.total_value)} color={C.amber} /><KPI label="Critical" value={chasing.critical} color={C.red} /><KPI label="Chase" value={chasing.medium} color={C.teal} /></div>
    <Card className="rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}><table className="w-full text-xs"><thead><tr style={{ background: '#FAFBFC' }} className="border-b">{['Policy', 'Business', 'Broker', 'Premium', 'Days', 'Action'].map(h => <th key={h} className="text-left py-2.5 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}</tr></thead><tbody>{chasing.queue.map(q => { const u = uc[q.urgency] || uc.medium; return (<tr key={q.policy_id} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}><td className="py-2.5 px-3 font-mono" style={{ color: C.blue }}>{q.policy_number}</td><td className="py-2.5 px-3 font-medium truncate max-w-[180px]" style={{ color: C.navy }}>{q.business_name}</td><td className="py-2.5 px-3" style={{ color: C.slate }}>{q.broker}</td><td className="py-2.5 px-3 font-mono">${q.premium?.toLocaleString()}</td><td className="py-2.5 px-3 font-mono font-semibold" style={{ color: u.color }}>{q.days_outstanding}d</td><td className="py-2.5 px-3"><span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: u.color + '12', color: u.color }}>{u.label}</span></td></tr>); })}</tbody></table></Card></div>);
}
function ReconciliationTab({ recon, data }) {
  if (!recon) return null;
  return (<div className="space-y-4"><div className="grid grid-cols-2 md:grid-cols-4 gap-3"><KPI label="Trust Balance" value={fmt$(recon.trust_balance)} color={C.blue} /><KPI label="Receipts" value={fmt$(recon.total_receipts)} color={C.green} /><KPI label="Disbursements" value={fmt$(recon.total_disbursements)} color={C.red} /><KPI label="Variance" value={fmt$(recon.variance)} color={Math.abs(recon.variance) < 1000 ? C.green : C.red} /></div>
    <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}><div className="flex items-center justify-between mb-3"><h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Trust Account Reconciliation</h3><span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium" data-testid="recon-status" style={{ background: (recon.status === 'reconciled' ? C.green : C.amber) + '12', color: recon.status === 'reconciled' ? C.green : C.amber }}><CheckCircle className="w-3.5 h-3.5" />{recon.status === 'reconciled' ? `Reconciled · ${recon.last_reconciled_month || ''}` : recon.status === 'never_reconciled' ? 'Never reconciled' : recon.status}</span></div><div className="grid grid-cols-2 gap-4"><Det label="Last Reconciled" value={recon.last_reconciled ? `${new Date(recon.last_reconciled).toLocaleString()} · ${recon.last_reconciled_by || ''}` : 'Never'} /><Det label="Expected Balance" value={fmt$(recon.expected_balance)} /><Det label="Actual Balance" value={fmt$(recon.trust_balance)} /><Det label="Items to Review" value={recon.items_to_review} /></div></Card></div>);
}

// ─── Loss Ratio (Actuarial, earned-premium basis) ───
function PackDispatch({ api }) {
  const [cfg, setCfg] = useState(null);
  const [recipients, setRecipients] = useState('');
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState('');
  useEffect(() => {
    api.get('/finance/reinsurer-pack/config').then(r => { setCfg(r.data); setRecipients(r.data.recipients.join(', ')); }).catch(() => {});
  }, [api]);
  if (!cfg) return <div />;
  const save = () => {
    api.put('/finance/reinsurer-pack/config', { recipients: recipients.split(',').map(s => s.trim()).filter(Boolean) })
      .then(r => { setCfg(r.data); setMsg('Recipients saved'); setTimeout(() => setMsg(''), 3000); }).catch(() => setMsg('Save failed'));
  };
  const sendNow = () => {
    setSending(true);
    api.post('/finance/reinsurer-pack/send')
      .then(r => {
        setMsg(r.data.status === 'sent' ? `Pack emailed to ${cfg.recipients.length} recipient${cfg.recipients.length !== 1 ? 's' : ''}` : `Send ${r.data.status}: ${r.data.reason || r.data.error || ''}`);
        api.get('/finance/reinsurer-pack/config').then(rr => setCfg(rr.data)).catch(() => {});
      })
      .catch(() => setMsg('Send failed')).finally(() => { setSending(false); setTimeout(() => setMsg(''), 6000); });
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="pack-dispatch">
      <span className="text-[11px] font-semibold" style={{ color: C.navy }}>Monthly pack to capacity providers</span>
      <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-bold" style={{ background: '#E7F6EF', color: C.green }}>AUTO · 1st of month</span>
      <input value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="provider@reinsurer.com, ..."
        className="px-2 py-1 border rounded-sm text-[11px] w-64" style={{ borderColor: 'rgba(136,150,168,0.3)' }} data-testid="pack-recipients-input" />
      <button onClick={save} className="px-2 py-1 rounded-sm text-[11px] font-semibold border hover:bg-slate-50" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.blue }} data-testid="pack-save-recipients-btn">Save</button>
      <button onClick={sendNow} disabled={sending} className="px-2 py-1 rounded-sm text-[11px] font-semibold text-white disabled:opacity-50" style={{ background: C.green }} data-testid="pack-send-now-btn">
        {sending ? 'Sending…' : 'Email Pack Now'}
      </button>
      {cfg.last_sent_at && <span className="text-[10px]" style={{ color: C.slate }} data-testid="pack-last-sent">Last sent {new Date(cfg.last_sent_at).toLocaleDateString()}</span>}
      {cfg.portal_url && (
        <button onClick={() => { navigator.clipboard.writeText(cfg.portal_url); setMsg('Portal link copied'); setTimeout(() => setMsg(''), 3000); }}
          className="px-2 py-1 rounded-sm text-[11px] font-semibold border hover:bg-slate-50" style={{ borderColor: 'rgba(136,150,168,0.3)', color: C.navy }}
          data-testid="pack-copy-portal-btn">Copy portal link</button>
      )}
      {msg && <span className="text-[10px] font-semibold" style={{ color: C.green }} data-testid="pack-dispatch-msg">{msg}</span>}
    </div>
  );
}

function LossRatioTab({ data, api }) {
  const [exporting, setExporting] = useState(false);
  if (!data) return null;
  const r = data.ratios; const p = data.premium; const c = data.claims; const b = data.basis;
  const tri = data.development_triangle;
  const exportPack = async () => {
    setExporting(true);
    try {
      const res = await api.get('/finance/reinsurer-pack', { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `underaiter-reinsurer-pack-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { console.error('Export failed:', e?.message); }
    setExporting(false);
  };
  return (
    <div className="space-y-4" data-testid="loss-ratio-tab">
      <ExperienceProvenance data={data.experience_provenance} testId="loss-ratio-provenance" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <PackDispatch api={api} />
        <button onClick={exportPack} disabled={exporting} data-testid="export-reinsurer-pack-btn"
          className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-[12px] font-semibold text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          style={{ background: C.blue }}>
          <Send className="w-3.5 h-3.5" />
          {exporting ? 'Building PDF…' : 'Export Reinsurer Pack (PDF)'}
        </button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Paid Loss Ratio" value={`${r.paid_loss_ratio}%`} color={C.green} />
        <KPI label="Incurred Loss Ratio" value={`${r.incurred_loss_ratio}%`} color={r.incurred_loss_ratio > 70 ? C.red : C.green} />
        <KPI label="Ultimate LR (incl. IBNR)" value={`${r.ultimate_loss_ratio}%`} color={r.ultimate_loss_ratio > 70 ? C.red : C.amber} />
        <KPI label="Net LR (QS 25%)" value={`${r.net_incurred_loss_ratio}%`} color={C.blue} />
      </div>

      <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)', background: '#FAFBFC' }} data-testid="loss-ratio-basis">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4" style={{ color: C.blue }} />
          <h3 className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Methodology &amp; Basis</h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
          <Det label="Numerator" value={b.numerator} />
          <Det label="Denominator" value={b.denominator} />
          <Det label="Reinsurance" value={b.reinsurance} />
          <Det label="IBNR" value={b.ibnr} />
        </div>
        <p className="text-[11px] mt-3 leading-relaxed" style={{ color: C.slate }}>{b.note}</p>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Premium &amp; Claims (as at {data.as_at})</h3>
          <div className="space-y-0.5">
            <PRow label="Written premium" value={p.written_premium} />
            <PRow label="Earned premium" value={p.earned_premium} bold />
            <PRow label="Unearned premium (UPR)" value={p.unearned_premium} neg />
            <PRow label="Claims paid" value={c.paid} />
            <PRow label="Outstanding reserves" value={c.reserves} />
            <PRow label="Incurred (paid + reserves)" value={c.incurred} bold />
            <PRow label="IBNR estimate" value={c.ibnr_estimate} neg />
            <PRow label="Ultimate incurred" value={c.ultimate} bold color={C.amber} />
          </div>
          <p className="text-[11px] mt-2" style={{ color: C.slate }}>Earned {p.earned_pct}% of written premium.</p>
        </Card>

        <Card className="p-4 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}>
          <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: C.navy }}>Incurred by Accident Quarter</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={data.accident_quarters.map(a => ({ name: a.quarter, Paid: a.paid, Reserves: a.reserves }))}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEF1F5" />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: C.slate }} />
              <YAxis tickFormatter={fmt$} tick={{ fontSize: 11, fill: C.slate }} width={48} />
              <Tooltip formatter={(v) => fmt$(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="Paid" stackId="a" fill={C.green} />
              <Bar dataKey="Reserves" stackId="a" fill={C.amber} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-4 rounded-md overflow-x-auto" style={{ border: '1px solid rgba(136,150,168,0.12)' }} data-testid="loss-triangle">
        <h3 className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: C.navy }}>Paid-Claims Development Triangle</h3>
        <p className="text-[11px] mb-3" style={{ color: C.slate }}>{tri.unit} — {tri.note}</p>
        <table className="w-full text-xs">
          <thead>
            <tr style={{ background: '#FAFBFC' }} className="border-b">
              <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>Accident Qtr</th>
              {tri.dev_periods.map(d => <th key={d} className="text-right py-2 px-3 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>Dev {d}</th>)}
            </tr>
          </thead>
          <tbody>
            {tri.rows.map(row => (
              <tr key={row.accident_quarter} className="border-b" style={{ borderColor: 'rgba(136,150,168,0.06)' }}>
                <td className="py-2 px-3 font-mono font-medium" style={{ color: C.blue }}>{row.accident_quarter}</td>
                {tri.dev_periods.map((d, i) => <td key={d} className="py-2 px-3 text-right font-mono" style={{ color: row.cumulative_paid[i] ? C.navy : '#C7CFD9' }}>{row.cumulative_paid[i] ? fmt$(row.cumulative_paid[i]) : '—'}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}


// ─── Shared ───
function KPI({ label, value, color }) { return (<Card className="px-4 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.12)' }}><p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p><p className="text-lg font-semibold mt-0.5 font-mono" style={{ color }}>{value}</p></Card>); }
function Det({ label, value }) { return (<div><p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p><p className="text-sm font-medium" style={{ color: C.navy }}>{value}</p></div>); }
function Sect({ title, children }) { return (<div className="mb-3"><p className="text-[10px] uppercase tracking-wider font-semibold mb-2 mt-3" style={{ color: C.teal }}>{title}</p>{children}</div>); }
function PRow({ label, value, bold, neg, color }) { return (<div className={`flex justify-between py-1 ${bold ? 'border-t pt-1.5' : ''}`} style={bold ? { borderColor: 'rgba(136,150,168,0.1)' } : {}}><span className={`text-xs ${bold ? 'font-semibold' : ''}`} style={{ color: color || (neg ? C.slate : C.navy) }}>{label}</span><span className={`text-xs font-mono ${bold ? 'font-bold' : ''}`} style={{ color: color || (neg ? C.red : C.navy) }}>{neg ? '(' : ''}{fmt$(value)}{neg ? ')' : ''}</span></div>); }
